import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Package, Layers, AlertTriangle, Sparkles, Tag } from "lucide-react";
import Shop from "@/pages/Shop";
import CustomOrderBuilder from "@/pages/CustomOrderBuilder";
import GuestCollectionBuilder from "@/pages/GuestCollectionBuilder";
import EconomyBuilder from "@/pages/EconomyBuilder";
import { useOrderNav } from "@/context/OrderNavContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { usePartner } from "@/context/PartnerContext";

export const OPTIONS = [
  { mode: "build", testid: "choose-single", icon: Package },
  { mode: "deals", testid: "choose-mixed", icon: Layers },
  { mode: "guest", testid: "choose-guest", icon: Sparkles },
  { mode: "economy", testid: "choose-economy", icon: Tag },
];

export const withLabels = (cats) => OPTIONS.map((o) => ({ ...o, ...cats[o.mode] }));

export default function OrderBuilder({ initialMode = null } = {}) {
  const { user } = useAuth();
  const trade = user?.role === "dropship"; // trade accounts have dynamic pricing — no multipacks
  const [mode, setMode] = useState(initialMode); // null | "build" | "deals" | "guest"
  const [dirty, setDirty] = useState(false); // true once a build has real selections
  const [confirmOpen, setConfirmOpen] = useState(false);
  const orderNav = useOrderNav();
  const { content, categories: cats } = useSiteContent();
  const { isPartner } = usePartner();

  // Trade accounts don't use Multipack Deals — send them to Build Your Box instead.
  useEffect(() => { if (trade && mode === "deals") setMode("build"); }, [trade, mode]);

  const labelled = withLabels(cats);
  const options = trade ? labelled.filter((o) => o.mode !== "deals") : labelled;

  const modeRef = useRef(mode);
  const dirtyRef = useRef(dirty);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const goChooser = useCallback(() => {
    setMode(null);
    setDirty(false);
    setConfirmOpen(false);
  }, []);

  // Return to the chooser. Warn first only if a build is part-way through.
  const requestChooser = useCallback(() => {
    if (!modeRef.current) return; // already on the chooser
    if (dirtyRef.current) setConfirmOpen(true);
    else goChooser();
  }, [goChooser]);

  // Let the Navbar's "Build Your Box" link defer to this behaviour while a box is open.
  useEffect(() => {
    if (!orderNav) return undefined;
    orderNav.current = requestChooser;
    return () => { if (orderNav.current === requestChooser) orderNav.current = null; };
  }, [orderNav, requestChooser]);

  const handleProgress = useCallback((v) => setDirty(!!v), []);
  const pickMode = (m) => { setMode(m); setDirty(false); };

  if (!mode) {
    return (
      <div data-testid="order-builder-page">
        {content?.hero_image_url && !isPartner && (
          <div className="mx-auto max-w-[1400px] sm:px-6 sm:pt-8">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="overflow-hidden border-y border-border bg-black sm:border" data-testid="home-banner">
              <img src={content.hero_image_url} alt="Flavours beyond reality" className="block h-auto w-full" />
            </motion.div>
          </div>
        )}
      <div className="mx-auto max-w-[1080px] px-6 py-12">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">Order</div>
          <h1 className="mt-1.5 font-heading text-4xl font-black tracking-tighter sm:text-5xl">How would you like to order?</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            {trade
              ? `Build your own box from every online size at your trade prices, browse our ${cats.guest.title}, or grab the budget ${cats.economy.title} range.`
              : `Build your own box from every online size, choose a ready-made ${cats.deals.title} deal, or try our new budget ${cats.economy.title} range.`}
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {options.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.button
                key={o.mode}
                data-testid={o.testid}
                onClick={() => pickMode(o.mode)}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.08 }}
                className="group relative flex flex-col overflow-hidden border border-border bg-card p-7 text-left transition-colors hover:border-primary"
              >
                {o.mode === "economy" && (
                  <span className="mono absolute right-0 top-0 bg-primary px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.15em] text-primary-foreground">New</span>
                )}
                <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
                <span className="flex h-12 w-12 items-center justify-center border border-primary/40 bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-6 w-6" />
                </span>
                <div className="mono mt-5 text-[10px] uppercase tracking-[0.2em] text-primary">{o.tagline}</div>
                <div className="mt-1 font-heading text-2xl font-black tracking-tight">{o.title}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{o.desc}</p>
                <span className="mono mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                  Start <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
      </div>
    );
  }

  const effMode = trade && mode === "deals" ? "build" : mode;
  const active = labelled.find((o) => o.mode === effMode) || labelled[0];

  return (
    <div data-testid="order-builder-page">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3 px-6 pt-6">
        <button
          onClick={requestChooser}
          data-testid="change-box-style"
          className="mono inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Change ordering option
        </button>
        <div className="mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="active-box-style">
          <span className="text-muted-foreground">Ordering: </span>
          <span className="font-bold text-primary">{active.title}</span>
          <span className="text-muted-foreground"> · {active.tagline}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={effMode}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
        >
          {effMode === "build"
            ? <Shop embedded eyebrow={cats.build.title} onProgress={handleProgress} />
            : effMode === "guest"
            ? <GuestCollectionBuilder />
            : effMode === "economy"
            ? <EconomyBuilder onProgress={handleProgress} />
            : <CustomOrderBuilder embedded onProgress={handleProgress} />}
        </motion.div>
      </AnimatePresence>

      {/* Reset warning when changing box style part-way through a build */}
      <AnimatePresence>
        {confirmOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid="reset-warning-backdrop"
            onClick={() => setConfirmOpen(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 6 }} transition={{ duration: 0.22 }}
              className="w-[min(26rem,calc(100vw-2rem))] border border-primary bg-card p-6 shadow-2xl shadow-black/60" data-testid="reset-warning-modal"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 text-primary">
                <AlertTriangle className="h-5 w-5" />
                <span className="mono text-[11px] uppercase tracking-[0.2em]">Change ordering option?</span>
              </div>
              <div className="mt-2 font-heading text-2xl font-black tracking-tight">Start again?</div>
              <p className="mono mt-2 text-sm text-muted-foreground">
                This will reset your current selections in this box. Items already added to your order are kept.
              </p>
              <button onClick={goChooser} data-testid="reset-confirm"
                className="mt-5 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                Reset &amp; choose ordering option
              </button>
              <button onClick={() => setConfirmOpen(false)} data-testid="reset-cancel"
                className="mono mt-3 w-full text-center text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary">
                Keep building
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
